import { useState, useRef, DragEvent, DragEventHandler } from 'react'
import { AssetReference } from '@opallabs/cassini'
import {
  Asset,
  renderIcon,
  kuiperClassNames,
  WithTooltip,
} from '@opallabs/kuiper'
import { useHorizontalScrollState } from '../../../../../components/kuiper/src/utils/hooks/useHorizontalScrollState'
import type { HorizontalScrollState } from '../../../../../components/kuiper/src/utils/hooks/useHorizontalScrollState'
import { styles } from './styles'

type DragStateProps = {
  dragging: boolean
  dragZone: number | null
}

interface Props {
  assets: AssetReference[]
  dragState: DragStateProps
  isPresenting: boolean
  maxAssets: number
  onDragComplete: DragEventHandler
  onDragEnter: DragEventHandler
  onDragStart: (e: DragEvent, asset: AssetReference) => void
  onExit: () => void
  onExpandAsset: (assetId: number) => void
  onRemoveAsset: (assetId: number) => void
  readOnly: boolean
  updateCaption: (value: string, assetId: number) => void
}

const onDragOver: DragEventHandler = (e) => e.preventDefault()

export const RemainingAssetsPreview: React.FC<Props> = ({
  assets = [],
  dragState,
  isPresenting,
  maxAssets,
  onDragComplete,
  onDragEnter,
  onDragStart,
  onExit,
  onExpandAsset,
  onRemoveAsset,
  readOnly,
  updateCaption,
}) => {
  const scrollArea = useRef<HTMLDivElement>(null)
  const [scrollLeft, setScrollLeft] = useState<number>(0)
  const { scrollBarState, maxScrollLeft }: HorizontalScrollState =
    useHorizontalScrollState(scrollArea, scrollLeft)
  const iconStyles = {
    height: 14,
    width: 14,
  }

  const onScrollLeft = () => {
    /* Scrolls to beginning of remaining assets view */
    if (!scrollArea.current) return
    scrollArea.current.scroll(0, 0)
  }

  const onScrollRight = () => {
    /* Scrolls to end of remaining assets view */
    if (!scrollArea.current) return
    scrollArea.current.scroll(maxScrollLeft.current || 0, 0)
  }

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */
  return (
    <div css={styles.remainingAssetsWrapper}>
      <div css={styles.scrollWrapper}>
        <ul css={styles.scroll}>
          <li
            onClick={onScrollLeft}
            className={kuiperClassNames(
              `remaining-asset-scroll${!scrollBarState.left ? '-disabled' : ''}`
            )}
          >
            <WithTooltip
              content="scroll to start"
              tooltipOptions={{ position: 'top' }}
              className="scroll"
            >
              {renderIcon('chevron-w', {
                ...iconStyles,
                ariaLabel: 'Scroll to Beginning Icon Trigger',
              })}
            </WithTooltip>
          </li>
          <li
            onClick={onScrollRight}
            className={kuiperClassNames(
              `remaining-asset-scroll${
                !scrollBarState.right ? '-disabled' : ''
              }`
            )}
          >
            <WithTooltip
              content="scroll to end"
              tooltipOptions={{ position: 'top' }}
              className="scroll"
            >
              {renderIcon('chevron-e', {
                ...iconStyles,
                ariaLabel: 'Scroll to End Icon Trigger',
              })}
            </WithTooltip>
          </li>
        </ul>
      </div>
      <div css={styles.header}>
        <div>
          Remaining Assets {maxAssets} - {assets.length + (maxAssets - 1)}
        </div>
        <div onClick={onExit}>
          {renderIcon('close-lg', {
            ...iconStyles,
            ariaLabel: 'Close Remaining Assets Popover Icon Trigger',
          })}
        </div>
      </div>
      <div
        css={styles.imageWrapper}
        ref={scrollArea}
        onScroll={() =>
          setScrollLeft(scrollArea.current ? scrollArea.current.scrollLeft : 0)
        }
      >
        {assets.length > 0 &&
          assets.map((asset, index) => {
            const zone = maxAssets - 1 + index
            return (
              <div
                key={`remaining-assets-${asset.id || 0}`}
                css={styles.itemWrapper}
              >
                <div
                  css={styles.draggableContainer}
                  data-zone-id={zone}
                  draggable={!readOnly && !isPresenting}
                  onDragStart={(e) => onDragStart(e, asset)}
                >
                  <div
                    className={kuiperClassNames('draggable-area')}
                    data-zone-id={zone}
                    css={styles.getDragAreaStyling(
                      dragState.dragging,
                      dragState.dragZone === zone
                    )}
                    onDragOver={onDragOver}
                    onDrop={onDragComplete}
                    onDragEnter={onDragEnter}
                  />
                  <Asset
                    imageFit="cover"
                    hasAssetOptions={true}
                    hasAssetOverlay={
                      asset &&
                      (!readOnly || isPresenting) &&
                      !dragState.dragging
                    }
                    assetReference={asset}
                    placeholder={true}
                    removeable={!isPresenting}
                    updateCaption={updateCaption}
                    overrideStyles={{
                      assetBase: styles.assetBase,
                    }}
                    onEmbiggen={() => onExpandAsset(asset.id || 0)}
                    onRemove={() => onRemoveAsset(asset.id || 0)}
                  />
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */
}
